import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Stack,
  Box,
  Alert,
  CircularProgress,
  Autocomplete,
  Chip,
  Typography,
} from '@mui/material';

function parseKeywords(raw) {
  const seen = new Set();
  const result = [];
  for (const token of raw.split(/[\n,]/)) {
    const trimmed = token.trim();
    if (!trimmed) continue;
    const lower = trimmed.toLowerCase();
    if (seen.has(lower)) continue;
    seen.add(lower);
    result.push(trimmed);
  }
  return result;
}

export default function AddKeywordDialog({
  open,
  onClose,
  onSave,
  keywordProjects,
  defaultProject,
}) {
  const [rawKeywords, setRawKeywords] = useState('');
  const [selectedScrapers, setSelectedScrapers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open && keywordProjects?.length && defaultProject) {
      const defaultOpt = keywordProjects.find((p) => p.scraper_name === defaultProject);
      setSelectedScrapers(defaultOpt ? [defaultOpt] : []);
    }
  }, [open, keywordProjects, defaultProject]);

  const keywords = useMemo(() => parseKeywords(rawKeywords), [rawKeywords]);
  const pairCount = keywords.length * selectedScrapers.length;

  const handleClose = () => {
    setRawKeywords('');
    setSelectedScrapers([]);
    setError(null);
    setResult(null);
    onClose();
  };

  const handleSave = async () => {
    if (keywords.length === 0) {
      setError('Enter at least one keyword');
      return;
    }
    if (selectedScrapers.length === 0) {
      setError('Select at least one scraper');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setResult(null);
      const res = await onSave(
        keywords,
        selectedScrapers.map((s) => s.scraper_name),
      );
      setResult(res);
      setTimeout(handleClose, 1800);
    } catch (err) {
      console.error('Error adding keywords:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Keywords</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {result && (
            <Alert severity={result.errors?.length ? 'warning' : 'success'}>
              {result.added?.length ? `Added ${result.added.length} keyword/scraper pair(s). ` : ''}
              {result.skipped?.length ? `Skipped ${result.skipped.length} (already exist). ` : ''}
              {result.errors?.length ? `${result.errors.length} error(s).` : ''}
            </Alert>
          )}

          <TextField
            label="Keywords"
            value={rawKeywords}
            onChange={(e) => setRawKeywords(e.target.value)}
            fullWidth
            autoFocus
            required
            multiline
            minRows={6}
            placeholder={'One keyword per line\nfraud\nsettlement\nprobe'}
            helperText="Enter one keyword per line. Commas are also accepted."
          />

          {keywords.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 0.75,
                maxHeight: 120,
                overflowY: 'auto',
              }}
            >
              {keywords.map((kw) => (
                <Chip key={kw.toLowerCase()} label={kw} size="small" />
              ))}
            </Box>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: -1 }}>
            <Typography variant="body2" color="text.secondary">
              Target Scrapers
            </Typography>
            <Box>
              <Button
                size="small"
                onClick={() => setSelectedScrapers(keywordProjects || [])}
                disabled={!keywordProjects?.length}
                sx={{ textTransform: 'none' }}
              >
                Select all
              </Button>
              <Button
                size="small"
                onClick={() => setSelectedScrapers([])}
                disabled={selectedScrapers.length === 0}
                sx={{ textTransform: 'none' }}
              >
                Clear
              </Button>
            </Box>
          </Box>

          <Autocomplete
            multiple
            options={keywordProjects || []}
            getOptionLabel={(opt) => opt.site_name || opt.scraper_name}
            value={selectedScrapers}
            onChange={(_e, val) => setSelectedScrapers(val)}
            isOptionEqualToValue={(opt, val) => opt.scraper_name === val.scraper_name}
            renderInput={(params) => (
              <TextField {...params} size="small" placeholder="Choose scrapers..." />
            )}
            renderTags={(value, getTagProps) =>
              value.map((opt, idx) => (
                <Chip
                  {...getTagProps({ index: idx })}
                  key={opt.scraper_name}
                  label={opt.site_name || opt.scraper_name}
                  size="small"
                />
              ))
            }
          />

          {keywords.length > 0 && selectedScrapers.length > 0 && (
            <Typography variant="body2" color="text.secondary">
              {keywords.length} keyword{keywords.length !== 1 ? 's' : ''} x {selectedScrapers.length} scraper
              {selectedScrapers.length !== 1 ? 's' : ''} = {pairCount} pair{pairCount !== 1 ? 's' : ''}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || keywords.length === 0 || selectedScrapers.length === 0}
          sx={{ backgroundColor: '#5f94de' }}
        >
          {saving ? (
            <CircularProgress size={20} />
          ) : (
            `Add ${keywords.length} Keyword${keywords.length !== 1 ? 's' : ''} to ${selectedScrapers.length} Scraper${selectedScrapers.length !== 1 ? 's' : ''}`
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
